// « Ajouter des hooks » (Studio → Hooks, docs/wip/SPEC-STUDIO.md): the owner drops many hook videos
// at once and each one becomes a `hook` element of its own (« Disponible »). A modal: the service
// when several fit, a drop zone (drag & drop, or click / Enter / Space for the file chooser), the
// files chosen (a file that is no mp4 / mov / webm video or is over 60 Mo is said refused and never
// sent; « Retirer » before sending; choosing again adds to the list), then one
// POST /api/studio/depts/:deptId/hooks?name=<file name> per file, the file as the body, strictly one
// after another (« Envoi 2/5… »). Every file sent: the modal closes; else the failed ones stay listed
// with their error for « Réessayer ». A page older than the server's build (staleClient) stops the batch.
import { h, iconButton, icon, button, select, field, setText, setAttr, classNames, uid } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { formatNumber } from '../../format.js';
import { filesOf, carriesFiles } from '../attachments.js';
import { UPLOAD_MAX_BYTES, holdUploads } from './item-parts.js';
import './strings.js';

const enc = encodeURIComponent;

/** The videos a hook may be (the server keeps mp4, mov, webm and m4v: server/studio/service.js createHook). */
export const HOOK_TYPES = Object.freeze(['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v']);
const HOOK_EXT = /\.(mp4|mov|webm|m4v)$/i;
/** `accept` of the file chooser. */
export const HOOK_ACCEPT = 'video/*';

/**
 * Why a file cannot become a hook (French, shown on its row), or null (pure): not an mp4 / mov /
 * webm video (its declared type, else its extension: a dropped file may come without a type),
 * empty, or over UPLOAD_MAX_BYTES (60 Mo). The server checks the content again.
 * @param {{name?: string, type?: string, size?: number}} file
 * @returns {string|null}
 */
export function hookProblem(file) {
  const type = String(file?.type || '').toLowerCase();
  const byName = HOOK_EXT.test(String(file?.name || ''));
  const video = HOOK_TYPES.includes(type) || (byName && (!type || type === 'application/octet-stream' || type.startsWith('video/')));
  if (!video) return t('studio.hooks.notVideo');
  const size = Number(file?.size);
  if (Number.isFinite(size) && size <= 0) return t('studio.hooks.empty');
  if (Number.isFinite(size) && size > UPLOAD_MAX_BYTES) return t('studio.hooks.tooBig');
  return null;
}

/**
 * Opens « Ajouter des hooks ».
 * @param {object} ctx app context
 * @param {object[]} where the services where the person may add hooks (studioDepts entries whose
 *   pipeline has a `hook` type, with operate); the first one is preselected
 * @param {{onDone?: (sent: number) => void}} [opts] onDone: after each batch (the list reloads)
 * @returns {import('../overlay.js').Layer|null}
 */
export function openAddHooks(ctx, where, { onDone } = {}) {
  if (!where?.length) return null;
  /** @type {Array<{id: number, file: File, state: 'ready'|'sending'|'sent'|'failed'|'refused', error: string}>} */
  let rows = [];
  let seq = 0;
  let busy = false;
  /** A batch left failures: the button reads « Réessayer » while they are all that is left to send. */
  let retrying = false;
  let closed = false;

  const deptSel = select(where.map((d) => ({ value: d.id, label: d.name })), { class: 'st-hooks-dept', value: where[0].id });
  const deptOf = () => where.find((d) => d.id === deptSel.value) || where[0];
  const picker = h('input', {
    type: 'file', multiple: true, accept: HOOK_ACCEPT, class: 'st-hooks-input', hidden: true, tabIndex: -1, 'aria-hidden': 'true',
  });
  // A button for the keyboard and screen readers: named by its sentence, described by its hint.
  const textId = uid('drop');
  const hintId = uid('hint');
  const zone = h('div', { class: 'st-hooks-drop', role: 'button', tabIndex: 0, 'aria-labelledby': textId, 'aria-describedby': hintId },
    h('span', { class: 'st-hooks-drop-icon', 'aria-hidden': 'true' }, icon('upload', { size: 22 })),
    h('span', { class: 'st-hooks-drop-text', id: textId }, t('studio.hooks.drop')),
    h('span', { class: 'st-hooks-drop-hint', id: hintId }, t('studio.hooks.dropHint')));
  const list = h('ul', { class: 'st-hooks-list', 'aria-label': t('studio.hooks.listLabel'), hidden: true });
  const status = h('p', { class: 'st-hooks-status', role: 'status', 'aria-live': 'polite', hidden: true });
  const box = h('div', { class: 'st-hooks' },
    where.length > 1 ? field(t('studio.new.dept'), deptSel) : null, zone, picker, list, status);

  const send = button(t('studio.hooks.sendNone'), { variant: 'primary', icon: 'upload', class: 'st-hooks-send', disabled: true, onClick: () => sendAll() });
  const sendLabel = send.querySelector('.btn-label') || send;
  // « Annuler » during a batch: the file on its way finishes, the others are not sent.
  const cancel = button(t('common.cancel'), { variant: 'ghost', class: 'st-hooks-cancel', onClick: () => modal.close('cancel') });

  const toSend = () => rows.filter((r) => r.state === 'ready' || r.state === 'failed');

  function idleLabel() {
    const next = toSend();
    if (!next.length) return t('studio.hooks.sendNone');
    if (retrying && next.every((r) => r.state === 'failed')) return t('studio.hooks.retry');
    return t('studio.hooks.send', { n: next.length });
  }

  function stateText(r) {
    return r.state === 'failed' || r.state === 'refused' ? r.error : t(`studio.hooks.state.${r.state}`);
  }

  function rowIcon(r) {
    if (r.state === 'sent') return 'check';
    if (r.state === 'failed' || r.state === 'refused') return 'alert';
    return 'film';
  }

  function render() {
    list.hidden = !rows.length;
    list.replaceChildren(...rows.map((r) => h('li', { class: classNames(['st-hook-row', `is-${r.state}`]), dataset: { state: r.state } },
      h('span', { class: 'st-hook-row-icon', 'aria-hidden': 'true' }, icon(rowIcon(r), { size: 16 })),
      h('span', { class: 'st-hook-row-main' },
        h('span', { class: 'st-hook-row-name', title: r.file.name }, r.file.name),
        h('span', { class: 'st-hook-row-meta' },
          h('span', { class: 'st-hook-row-size mono-label' }, t('studio.hooks.size', { n: formatNumber((Number(r.file.size) || 0) / 1048576, 1) })),
          h('span', { class: 'st-hook-row-state' }, stateText(r)))),
      r.state === 'sent' || r.state === 'sending' ? null
        : iconButton('close', t('studio.hooks.remove', { name: r.file.name }), { class: 'st-hook-row-remove', size: 14, disabled: busy, onClick: () => remove(r.id) }))));
    zone.classList.toggle('is-disabled', busy);
    setAttr(zone, 'aria-disabled', busy ? 'true' : null);
    deptSel.disabled = busy;
    send.disabled = busy || !toSend().length;
    if (!busy) setText(sendLabel, idleLabel());
  }

  function say(message, { error = false } = {}) {
    setText(status, message || '');
    status.hidden = !message;
    status.classList.toggle('is-error', error);
    setAttr(status, 'role', error ? 'alert' : 'status');
  }

  /** Files chosen or dropped join the list (once per name and size); refused ones say why. */
  function add(files) {
    if (busy || closed) return;
    let added = 0;
    for (const file of Array.from(files || [])) {
      if (!file || rows.some((r) => r.file.name === file.name && r.file.size === file.size)) continue;
      const problem = hookProblem(file);
      seq += 1;
      rows.push({ id: seq, file, state: problem ? 'refused' : 'ready', error: problem || '' });
      added += 1;
    }
    if (!added) return;
    say('');
    render();
  }

  function remove(id) {
    if (busy) return;
    rows = rows.filter((r) => r.id !== id);
    render();
    zone.focus?.();
  }

  function choose() {
    if (busy || closed) return;
    picker.click?.();
  }

  zone.addEventListener('click', choose);
  zone.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    e.preventDefault?.();
    choose();
  });
  picker.addEventListener('change', () => {
    add(picker.files);
    try { picker.value = ''; } catch { /* some browsers refuse: harmless */ }
  });
  // Dragged files: the zone shows .is-dragover; while a batch runs a drop is refused, never left to
  // the browser (it would open the video in place of the office and stop the sending): the rest of
  // the modal is guarded below.
  const over = (e) => {
    if (!carriesFiles(e.dataTransfer)) return;
    e.preventDefault?.();
    if (e.dataTransfer) e.dataTransfer.dropEffect = busy ? 'none' : 'copy';
    if (!busy) zone.classList.add('is-dragover');
  };
  zone.addEventListener('dragenter', over);
  zone.addEventListener('dragover', over);
  zone.addEventListener('dragleave', () => zone.classList.remove('is-dragover'));
  zone.addEventListener('drop', (e) => {
    zone.classList.remove('is-dragover');
    if (!carriesFiles(e.dataTransfer)) return;
    e.preventDefault?.();
    add(filesOf(e.dataTransfer));
  });

  /** Sends the files to send, one after another; the list reloads after (onDone). */
  async function sendAll() {
    if (busy || closed) return;
    const batch = toSend();
    if (!batch.length) return;
    busy = true;
    const release = holdUploads();
    const deptId = deptOf().id;
    let sent = 0;
    let stale = null;
    say('');
    try {
      for (const [i, r] of batch.entries()) {
        if (closed) break;
        r.state = 'sending';
        r.error = '';
        render();
        setText(sendLabel, t('studio.files.sending', { i: i + 1, n: batch.length }));
        try {
          await ctx.api.upload(ctx.api.withQuery(`/api/studio/depts/${enc(deptId)}/hooks`, { name: r.file.name }), r.file);
          r.state = 'sent';
          sent += 1;
        } catch (err) {
          r.state = 'failed';
          r.error = err?.message || String(err);
          // This page is older than the server: every next file would be refused the same way.
          if (err?.staleClient) { stale = err; break; }
        }
      }
    } finally {
      release();
      busy = false;
    }
    const failed = rows.filter((r) => r.state === 'failed').length;
    retrying = failed > 0;
    if (sent) ctx.toast.success(t('studio.hooks.done', { n: sent }), { timeout: 3000 });
    onDone?.(sent);
    if (closed) {
      // Closed during the batch (« Annuler », Esc, the page around): the files left behind are said.
      const left = rows.filter((r) => r.state === 'ready' || r.state === 'failed').length;
      if (left) ctx.toast.warn(t('studio.hooks.notSent', { n: left }), { timeout: 6000 });
      return;
    }
    if (!stale && !failed && !rows.some((r) => r.state === 'ready')) {
      modal.close('saved');
      return;
    }
    render();
    if (stale) say(stale.message, { error: true });
    else if (failed) say(t('studio.hooks.failed', { n: failed }), { error: true });
  }

  const modal = ctx.overlay.openModal({
    title: t('studio.hooks.title'), size: 'md', content: box, footer: [cancel, send],
    initialFocus: where.length > 1 ? deptSel : zone, className: 'st-hooks-modal',
  });
  modal.onClose(() => { closed = true; });
  // A file dropped anywhere else in the modal (the list, the footer, the dimmed page around it) is
  // never left to the browser either: it joins the list (refused while a batch runs).
  const guard = (e) => {
    if (!carriesFiles(e.dataTransfer) || zone.contains?.(e.target)) return;
    e.preventDefault?.();
    if (e.type === 'drop') add(filesOf(e.dataTransfer));
    else if (e.dataTransfer) e.dataTransfer.dropEffect = busy ? 'none' : 'copy';
  };
  const host = modal.el || box;
  host.addEventListener('dragover', guard);
  host.addEventListener('drop', guard);
  return modal;
}
